/**
 * Client for company lists — `/api/web/v2/company-lists` and
 * `/api/web/v2/searches/{id}/company-lists` (epic 2026-09-25-chopbook-company-lists).
 *
 * A company list is what a Chopbook upload or a CSV import leaves on a search,
 * the sibling of a people list. Its members are shared company records, so
 * removing one from a list never deletes the company. Uploads themselves go
 * through `pitchbook.client` (`createUpload` with `name` + `source`), which
 * creates the list.
 *
 * Types come from `@nu-assist/contracts` (generated from the API's OpenAPI).
 * Auth: makeAuthedFetch (401 → refresh → retry once), as every web client.
 */
import type {
  CompanyListMembersPage,
  CompanyListOwnersResponse,
  CompanyListSummary,
  CompanyListsPage,
  CompanyPeople,
  CompanyRecord,
  CompanyRef,
  CompanySearchResult,
  CopyCompaniesResult,
  LinkedinMatch,
  LinkedinUnmatchedCompanies,
  PositionCompanyLink,
} from "@nu-assist/contracts";
import { makeAuthedFetch } from "@nu-assist/shared-fe";

import type { FetchLike } from "./imports.client";

export type CompanyMemberSortKey =
  | "name"
  | "hq_country"
  | "sector"
  | "founded_year"
  | "employees"
  | "revenue_usd_m"
  | "added_at"
  | "people_known";

/** The list index's sortable columns (People Lists' `ImportListSortKey`). */
export type CompanyListSortKey = "created_at" | "name" | "company_count" | "created_by_email";

export interface PageParams {
  page?: number;
  page_size?: number;
}

/** Filters on the list index. Both endpoints take `q`; only the firm-wide
 *  one takes `search_id_thrive` (a search's tab is already one search). */
export interface ListIndexParams extends PageParams {
  /** Lists whose name contains this, case-insensitive. Blank = no filter. */
  q?: string;
  /** "Imported by": `you` | `all` | a recruiter's email. The API defaults to `all`. */
  owner?: string;
  /** Omitted = newest first. */
  sort_key?: CompanyListSortKey;
  sort_dir?: "asc" | "desc";
}

export interface ListAllParams extends ListIndexParams {
  /** Only the lists made in this Thrive search. */
  search_id_thrive?: string;
}

/** The list detail page's filters; every one optional, combined with AND. */
export interface MemberFilterParams {
  /** Company name or website contains this, case-insensitive. */
  q?: string;
  employees_min?: number;
  employees_max?: number;
  /** In MILLIONS of USD, the unit of `revenue_usd_m`. */
  revenue_min?: number;
  revenue_max?: number;
  /** At least one person we know there (`yes`), or none (`no`). */
  we_know?: "yes" | "no";
  /** Tied to a LinkedIn page, or not. */
  linkedin?: "matched" | "not_matched";
}

export interface MemberParams extends PageParams, MemberFilterParams {
  sort_key?: CompanyMemberSortKey;
  sort_dir?: "asc" | "desc";
}

/** Keys per `/avatars/presign` call: ~80 bytes each keeps a batch well under
 *  the shared ALB WAF's ~8 KB body cap (the same bound people avatars use). */
const PRESIGN_MAX_KEYS_PER_REQUEST = 50;

export interface CompanyListsClientConfig {
  apiBase: string;
  fetchImpl?: FetchLike;
}

/** A failed request. `detail` carries FastAPI's message when it sent one. */
export class CompanyListsApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly url: string,
    public readonly detail: string | null = null
  ) {
    super(detail ?? `Company lists request failed (${status})`);
    this.name = "CompanyListsApiError";
    Object.setPrototypeOf(this, CompanyListsApiError.prototype);
  }
}

function buildQuery(params: object): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

async function readDetail(res: Response): Promise<string | null> {
  try {
    const body: unknown = await res.json();
    const detail = (body as { detail?: unknown } | null)?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
      const msg = (detail[0] as { msg?: unknown } | undefined)?.msg;
      return typeof msg === "string" ? msg : null;
    }
    return null;
  } catch {
    return null;
  }
}

/** The filename from `Content-Disposition: attachment; filename="x.csv"`. */
function filenameOf(res: Response, fallback: string): string {
  const header = res.headers.get("content-disposition") ?? "";
  const match = /filename="([^"]+)"/.exec(header);
  return match?.[1] ?? fallback;
}

export function createCompanyListsClient(cfg: CompanyListsClientConfig) {
  const authedFetch: FetchLike =
    cfg.fetchImpl ??
    makeAuthedFetch({
      apiBase: cfg.apiBase,
      onSessionExpired: () => window.location.assign("/login"),
    });

  async function send(method: string, path: string, body?: unknown): Promise<Response> {
    const url = `${cfg.apiBase}${path}`;
    const res = await authedFetch(url, {
      method,
      credentials: "include",
      headers: body !== undefined ? { "content-type": "application/json" } : undefined,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) throw new CompanyListsApiError(res.status, url, await readDetail(res));
    return res;
  }

  async function json<T>(method: string, path: string, body?: unknown): Promise<T> {
    return (await (await send(method, path, body)).json()) as T;
  }

  const one = (listId: string) => `/api/web/v2/company-lists/${encodeURIComponent(listId)}`;
  const ofSearch = (searchId: string) =>
    `/api/web/v2/searches/${encodeURIComponent(searchId)}/company-lists`;

  return {
    /** Every company list in the firm, newest first; optionally narrowed by
     *  name (`q`) and to one search (`search_id_thrive`). */
    listAll(params: ListAllParams = {}): Promise<CompanyListsPage> {
      return json("GET", `/api/web/v2/company-lists${buildQuery(params)}`);
    },
    /** Everyone who made a company list: the "Imported by" menu's options. */
    listOwners(): Promise<CompanyListOwnersResponse> {
      return json("GET", "/api/web/v2/company-lists/owners");
    },
    /** The company lists of one search, newest first; optionally by name (`q`). */
    listForSearch(searchId: string, params: ListIndexParams = {}): Promise<CompanyListsPage> {
      return json("GET", `${ofSearch(searchId)}${buildQuery(params)}`);
    },
    /** An empty list on a search. */
    create(searchId: string, name: string): Promise<CompanyListSummary> {
      return json("POST", ofSearch(searchId), { name });
    },
    get(listId: string): Promise<CompanyListSummary> {
      return json("GET", one(listId));
    },
    rename(listId: string, name: string): Promise<CompanyListSummary> {
      return json("PATCH", one(listId), { name });
    },
    // `remove`, `removeMember` and `copyMembers` have no UI caller since the
    // web stopped offering delete / remove / copy (2026-10-05, #1000). They
    // stay because the endpoints stay, and the E2E cleanup deletes its lists
    // through the same API.
    /** Deletes the list and its memberships; companies stay. */
    async remove(listId: string): Promise<void> {
      await send("DELETE", one(listId));
    },
    members(listId: string, params: MemberParams = {}): Promise<CompanyListMembersPage> {
      return json("GET", `${one(listId)}/companies${buildQuery(params)}`);
    },
    async removeMember(listId: string, companyId: string): Promise<void> {
      await send("DELETE", `${one(listId)}/companies/${encodeURIComponent(companyId)}`);
    },
    copyMembers(
      listId: string,
      targetListId: string,
      companyIds: string[]
    ): Promise<CopyCompaniesResult> {
      return json("POST", `${one(listId)}/companies/copy`, {
        target_list_id: targetListId,
        company_ids: companyIds,
      });
    },
    /** The shared company record: fields, platform links, lists, people counts. */
    record(companyId: string): Promise<CompanyRecord> {
      return json("GET", `/api/web/v2/companies/${encodeURIComponent(companyId)}`);
    },
    /** Company records for LinkedIn company ids (a person's jobs), ≤100 per call. */
    async companiesByLinkedinIds(
      linkedinIds: string[],
      companyIds: string[] = []
    ): Promise<CompanyRef[]> {
      const out = await json<{ items: CompanyRef[] }>(
        "POST",
        "/api/web/v2/companies/by-linkedin-ids",
        { linkedin_ids: linkedinIds.slice(0, 100), company_ids: companyIds.slice(0, 100) }
      );
      return out.items;
    },
    /** The company picker: names containing `q` (2+ characters), prefix first. */
    async searchCompanies(q: string): Promise<CompanySearchResult[]> {
      const qs = new URLSearchParams({ q, limit: "10" });
      return (
        await json<{ items: CompanySearchResult[] }>("GET", `/api/web/v2/companies/search?${qs}`)
      ).items;
    },
    /** Link ONE job to a company by hand: an existing record, or a new one from a name. */
    linkPosition(
      positionId: string,
      target: { company_id: string } | { new_company_name: string }
    ): Promise<PositionCompanyLink> {
      return json(
        "POST",
        `/api/web/v2/people/positions/${encodeURIComponent(positionId)}/company`,
        target
      );
    },
    /** Remove a job's hand link; the company stays. */
    unlinkPosition(positionId: string): Promise<PositionCompanyLink> {
      return json(
        "DELETE",
        `/api/web/v2/people/positions/${encodeURIComponent(positionId)}/company`
      );
    },
    /** People from our lists whose work history names the company. */
    people(companyId: string): Promise<CompanyPeople> {
      return json("GET", `/api/web/v2/companies/${encodeURIComponent(companyId)}/people`);
    },
    /** Short-lived GET URLs for stored company logos (`/companies/logos/presign`).
     *  Batched; a failed batch just leaves those companies on their initials. */
    async presignLogos(s3Keys: string[]): Promise<Record<string, string>> {
      const batches: string[][] = [];
      for (let i = 0; i < s3Keys.length; i += PRESIGN_MAX_KEYS_PER_REQUEST) {
        batches.push(s3Keys.slice(i, i + PRESIGN_MAX_KEYS_PER_REQUEST));
      }
      const results = await Promise.allSettled(
        batches.map((batch) =>
          json<{ urls: Record<string, string> }>("POST", "/api/web/v2/companies/logos/presign", {
            s3_keys: batch,
          })
        )
      );
      const urls: Record<string, string> = {};
      for (const r of results) if (r.status === "fulfilled") Object.assign(urls, r.value.urls);
      return urls;
    },
    /** Short-lived GET URLs for people's stored avatars (`/avatars/presign`),
     *  for "People we know here". Batched; a failed batch leaves those people
     *  on their initials. */
    async presignAvatars(s3Keys: string[]): Promise<Record<string, string>> {
      const batches: string[][] = [];
      for (let i = 0; i < s3Keys.length; i += PRESIGN_MAX_KEYS_PER_REQUEST) {
        batches.push(s3Keys.slice(i, i + PRESIGN_MAX_KEYS_PER_REQUEST));
      }
      const results = await Promise.allSettled(
        batches.map((batch) =>
          json<{ urls: Record<string, string> }>("POST", "/api/web/v2/avatars/presign", {
            s3_keys: batch,
          })
        )
      );
      const urls: Record<string, string> = {};
      for (const r of results) if (r.status === "fulfilled") Object.assign(urls, r.value.urls);
      return urls;
    },
    /** The list's companies PitchBook ties to a LinkedIn page ChopLink has not read. */
    linkedinUnmatched(
      listId: string,
      limit?: number,
      offset?: number
    ): Promise<LinkedinUnmatchedCompanies> {
      return json("GET", `${one(listId)}/linkedin-unmatched${buildQuery({ limit, offset })}`);
    },
    /** Join a PitchBook company with the LinkedIn record ChopLink saved for it. */
    matchLinkedin(companyId: string, linkedinId: string): Promise<LinkedinMatch> {
      return json("POST", `/api/web/v2/companies/${encodeURIComponent(companyId)}/linkedin-match`, {
        linkedin_id: linkedinId,
      });
    },
    /** The list as CSV (PitchBook headers, re-importable) and its filename. */
    async exportCsv(listId: string): Promise<{ filename: string; blob: Blob }> {
      const res = await send("GET", `${one(listId)}/export`);
      return { filename: filenameOf(res, "company-list.csv"), blob: await res.blob() };
    },
  };
}

export type CompanyListsClient = ReturnType<typeof createCompanyListsClient>;
